import { memo, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Avatar, Button, Modal, ModalBody, ModalContent, ModalFooter } from '@heroui/react';
import {
  CHAT_GROUP_AVATAR_ACCEPT,
  CHAT_GROUP_MAX_PARTICIPANTS,
  CHAT_MODAL_WIDTH_CLASS,
  CHAT_USERNAME_SEARCH_MAX,
} from '../../config/constants';
import { buildUserPresenceLabel } from '../../lib/formatParticipantPresence';
import type { ChatParticipant, ChatUser } from '../../model/types';
import {
  IconCamera,
  IconCheckCircle,
  IconChevronDown,
  IconClose,
  IconSearch,
  IconUpload,
  IconUser,
  IconUsers,
} from '../icons';

export type EditGroupChatModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onTitleChange: (value: string) => void;
  status: 'active' | 'closed';
  onStatusChange: (value: 'active' | 'closed') => void;
  avatarFile: File | null;
  avatarPreviewUrl: string | null;
  onAvatarFileChange: (file: File | null) => void;
  participants: ChatParticipant[];
  addedUsers: ChatUser[];
  removedUserIds: number[];
  onRemoveParticipant: (userId: number) => void;
  onUndoRemoveParticipant: (userId: number) => void;
  pickerUsers: ChatUser[];
  search: string;
  onSearchChange: (value: string) => void;
  onToggleAddUser: (user: ChatUser) => void;
  isLoadingUsers: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  canSave: boolean;
  onSubmit: () => void | Promise<void>;
  canDelete?: boolean;
  isDeleting?: boolean;
  onDelete?: () => void | Promise<void>;
};

export const EditGroupChatModal = memo(
  ({
    isOpen,
    onClose,
    title,
    onTitleChange,
    status,
    onStatusChange,
    avatarFile,
    avatarPreviewUrl,
    onAvatarFileChange,
    participants,
    addedUsers,
    removedUserIds,
    onRemoveParticipant,
    onUndoRemoveParticipant,
    pickerUsers,
    search,
    onSearchChange,
    onToggleAddUser,
    isLoadingUsers,
    isSubmitting,
    errorMessage,
    canSave,
    onSubmit,
    canDelete = false,
    isDeleting = false,
    onDelete,
  }: EditGroupChatModalProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [participantsExpanded, setParticipantsExpanded] = useState(true);
    const [avatarError, setAvatarError] = useState<string | null>(null);

    const activeCount =
      participants.filter((participant) => !removedUserIds.includes(participant.user.id)).length +
      addedUsers.length;

    const pickerUsersFiltered = useMemo(() => {
      const existingIds = new Set([
        ...participants.map((item) => item.user.id),
        ...addedUsers.map((item) => item.id),
      ]);

      return pickerUsers.filter((user) => !existingIds.has(user.id));
    }, [addedUsers, participants, pickerUsers]);

    useEffect(() => {
      if (!isOpen) {
        setAvatarError(null);
      }
    }, [isOpen]);

    const handleClose = () => {
      if (isSubmitting) return;
      onClose();
    };

    const handleAvatarPick = (file: File | null) => {
      setAvatarError(null);

      if (!file) {
        onAvatarFileChange(null);
        return;
      }

      if (!file.type.startsWith('image/')) {
        setAvatarError('Only PNG or JPG images are allowed');
        return;
      }

      onAvatarFileChange(file);
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        placement="center"
        backdrop="opaque"
        hideCloseButton={isSubmitting}
        classNames={{
          base: `${CHAT_MODAL_WIDTH_CLASS} rounded-[28px] p-0`,
          closeButton: 'top-7 right-7 text-black/50 hover:bg-transparent hover:text-black',
        }}
      >
        <ModalContent className="overflow-hidden p-0">
          <ModalBody className="px-9 pb-0 pt-8">
            <div className="flex flex-col gap-6">
              <h2 className="text-[28px] font-semibold text-[#1E1E1E]">Edit group</h2>

              <div className="flex flex-col items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    'relative flex h-[120px] w-[120px] items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-[#D0D0D0] bg-[#FAFAFA] transition-colors hover:border-[#27245F]',
                    avatarPreviewUrl && 'border-solid border-transparent',
                  )}
                  aria-label="Change group avatar"
                >
                  {avatarPreviewUrl ? (
                    <>
                      <img src={avatarPreviewUrl} alt="" className="h-full w-full object-cover" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/35">
                        <IconCamera className="text-white" />
                      </span>
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-2 px-3 text-center text-[#8B8B8B]">
                      <IconUpload className="text-[#27245F]" />
                      <span className="text-[13px] leading-tight">
                        PNG, JPG
                        <br />
                        (max 1024x1024px)
                      </span>
                    </div>
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={CHAT_GROUP_AVATAR_ACCEPT}
                  className="hidden"
                  onChange={(event) => {
                    handleAvatarPick(event.target.files?.[0] ?? null);
                    event.target.value = '';
                  }}
                />
                {avatarError ? <p className="text-sm text-danger">{avatarError}</p> : null}
              </div>

              <label className="flex flex-col gap-2">
                <span className="text-[15px] font-medium text-[#1E1E1E]">Group name</span>
                <input
                  value={title}
                  onChange={(event) => onTitleChange(event.target.value)}
                  placeholder="Enter group name"
                  className="h-[52px] rounded-2xl border border-[#E4E4E4] px-4 text-base text-[#1E1E1E] outline-none placeholder:text-[#B0B0B0] focus:border-[#27245F]"
                />
              </label>

              <div className="flex flex-col gap-2">
                <span className="text-[15px] font-medium text-[#1E1E1E]">Status</span>
                <div className="flex gap-2">
                  {(['active', 'closed'] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => onStatusChange(value)}
                      className={cn(
                        'h-[44px] flex-1 rounded-xl border text-[15px] font-medium capitalize transition-colors',
                        status === value
                          ? 'border-[#27245F] bg-[#27245F] text-white'
                          : 'border-[#E4E4E4] bg-white text-[#1E1E1E] hover:border-[#27245F]',
                      )}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => setParticipantsExpanded((value) => !value)}
                  className="flex w-full items-center justify-between py-1 text-left"
                >
                  <span className="flex items-center gap-2 text-[16px] font-medium text-[#1E1E1E]">
                    <IconUsers className="text-[#27245F]" />
                    Participants
                    <span className="text-[13px] font-normal text-[#8B8B8B]">
                      ({activeCount}/{CHAT_GROUP_MAX_PARTICIPANTS})
                    </span>
                  </span>
                  <IconChevronDown
                    className={cn(
                      'text-[#1E1E1E] transition-transform',
                      participantsExpanded && 'rotate-180',
                    )}
                  />
                </button>

                {participantsExpanded ? (
                  <div className="flex flex-col gap-4">
                    <div className="space-y-2">
                      {participants.map((participant) => {
                        const fullName =
                          [participant.user.firstName, participant.user.lastName]
                            .filter(Boolean)
                            .join(' ')
                            .trim() || participant.user.username;
                        const isRemoved = removedUserIds.includes(participant.user.id);
                        const presence = buildUserPresenceLabel(participant.user);

                        return (
                          <div
                            key={participant.participantId}
                            className={cn(
                              'flex items-center gap-3 rounded-2xl px-3 py-2',
                              isRemoved ? 'bg-[#FFF4F4] opacity-70' : 'bg-[#F6F6F6]',
                            )}
                          >
                            <div className="relative shrink-0">
                              <Avatar
                                name={fullName}
                                src={participant.user.avatarUrl ?? undefined}
                                size="sm"
                                showFallback
                              />
                              {presence?.online ? (
                                <span
                                  className="absolute right-0 bottom-0 size-2 rounded-full border-2 border-white bg-success"
                                  aria-hidden
                                />
                              ) : null}
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block truncate text-[15px] font-medium text-[#1E1E1E]">
                                {fullName}
                              </span>
                              {presence ? (
                                <span
                                  className={cn(
                                    'block truncate text-[12px]',
                                    presence.online
                                      ? 'font-medium text-success'
                                      : 'text-[#8B8B8B]',
                                  )}
                                >
                                  {presence.lastSeenLabel}
                                </span>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#757575] hover:bg-black/[0.06]"
                              aria-label={isRemoved ? 'Undo remove participant' : 'Remove participant'}
                              onClick={() =>
                                isRemoved
                                  ? onUndoRemoveParticipant(participant.user.id)
                                  : onRemoveParticipant(participant.user.id)
                              }
                            >
                              <IconClose className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })}

                      {addedUsers.map((user) => {
                        const fullName =
                          [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
                          user.username;

                        return (
                          <div
                            key={`added-${user.id}`}
                            className="flex items-center gap-3 rounded-2xl bg-[#EEF0FF] px-3 py-2"
                          >
                            <Avatar
                              name={fullName}
                              src={user.avatarUrl ?? undefined}
                              size="sm"
                              showFallback
                            />
                            <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-[#1E1E1E]">
                              {fullName}
                            </span>
                            <button
                              type="button"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#757575] hover:bg-black/[0.06]"
                              aria-label="Remove added participant"
                              onClick={() => onToggleAddUser(user)}
                            >
                              <IconClose className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex items-center gap-3 rounded-2xl bg-[#F6F6F6] px-4 py-4">
                      <IconSearch className="shrink-0 text-[#8B8B8B]" />
                      <input
                        value={search}
                        maxLength={CHAT_USERNAME_SEARCH_MAX}
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder="Username in academic list, or exact ID / name"
                        className="w-full bg-transparent text-base text-[#1E1E1E] outline-none placeholder:text-[#B0B0B0]"
                        disabled={activeCount >= CHAT_GROUP_MAX_PARTICIPANTS}
                      />
                    </div>

                    {activeCount >= CHAT_GROUP_MAX_PARTICIPANTS ? (
                      <p className="text-sm text-[#8B8B8B]">Maximum {CHAT_GROUP_MAX_PARTICIPANTS} participants</p>
                    ) : null}

                    <div className="max-h-[220px] overflow-y-auto pr-4">
                      <ul className="flex flex-col gap-2">
                        {pickerUsersFiltered.map((user) => {
                          const isSelected = addedUsers.some((item) => item.id === user.id);
                          const fullName =
                            [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
                            user.username;

                          return (
                            <li key={user.id}>
                              <button
                                type="button"
                                onClick={() => onToggleAddUser(user)}
                                className={cn(
                                  'flex w-full items-center gap-4 rounded-3xl px-4 py-3 text-left transition-colors',
                                  isSelected ? 'bg-[#F1F1F1]' : 'bg-white hover:bg-[#F7F7F7]',
                                )}
                              >
                                <Avatar
                                  name={fullName}
                                  src={user.avatarUrl ?? undefined}
                                  size="md"
                                  showFallback
                                  className="h-11 w-11 shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 text-[#1E1E1E]">
                                    <IconUser className="shrink-0" />
                                    <span className="truncate text-[15px] font-medium">{fullName}</span>
                                  </div>
                                  {user.badge ? (
                                    <span className="mt-0.5 block truncate pl-6 text-[13px] text-[#8B8B8B]">
                                      {user.badge}
                                    </span>
                                  ) : (
                                    <span className="mt-0.5 block truncate pl-6 text-[13px] text-[#8B8B8B]">
                                      @{user.username}
                                    </span>
                                  )}
                                </div>
                                {isSelected ? (
                                  <IconCheckCircle className="shrink-0 text-[#27245F]" />
                                ) : null}
                              </button>
                            </li>
                          );
                        })}
                      </ul>

                      {!isLoadingUsers && search.trim() && !pickerUsersFiltered.length ? (
                        <div className="py-6 text-center text-sm text-default-500">
                          No matching users
                        </div>
                      ) : null}

                      {isLoadingUsers ? (
                        <div className="py-6 text-center text-sm text-default-500">Loading...</div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              {errorMessage ? <p className="text-sm text-danger">{errorMessage}</p> : null}

              {canDelete && onDelete ? (
                <button
                  type="button"
                  disabled={isSubmitting || isDeleting}
                  onClick={() => void onDelete()}
                  className="w-full rounded-xl border border-danger py-3 text-[15px] font-medium text-danger transition-colors hover:bg-danger/5 disabled:opacity-60"
                >
                  {isDeleting ? 'Deleting group...' : 'Delete group permanently'}
                </button>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter className="mt-8 flex justify-between gap-5 border-t border-[#ECECEC] px-9 py-6">
            <Button
              variant="bordered"
              className="h-[52px] min-w-[230px] rounded-[12px] border-[#1E1E1E] bg-white text-[16px] font-medium text-[#1E1E1E]"
              isDisabled={isSubmitting}
              onPress={handleClose}
            >
              Cancel
            </Button>
            <Button
              className={cn(
                'h-[52px] min-w-[230px] rounded-[12px] text-[16px] font-medium text-white',
                canSave ? 'bg-[#27245F]' : 'bg-[#C8C8C8]',
              )}
              isDisabled={!canSave}
              isLoading={isSubmitting}
              onPress={() => void onSubmit()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);

EditGroupChatModal.displayName = 'EditGroupChatModal';
